import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DateSelector from './DateSelector';
import StreamsList from './StreamsList';
import StreamModal from './StreamModal';
import { fetchStreamData } from '../utils/dataMapper';
import { calculateAllStreamStatuses } from '../utils/statusCalculator';
import { getStreamStatus } from '../utils/statusCalculator';
import '../styles/StreamsContainer.css';
import '../styles/LoadingMessage.css';

const StreamsContainer = () => {
  // API URL (本番環境を判定)
  const isProduction = window.location.hostname === 'schedule.stpr.com';
  const API_URL = isProduction
    ? 'https://schedule-share-json.s3.ap-northeast-1.amazonaws.com/schedule/schedule_v2.json'
    : 'https://dev-schedule-share-json.s3.ap-northeast-1.amazonaws.com/schedule/schedule_v2.json';
  // const API_URL = 'https://script.googleusercontent.com/macros/echo?user_content_key=AehSKLiB0GcO4fGkGWTVYrHFgoeVcaymuKWkhGHb1pEhuDx9P_f_Kt0AXkVOC1b3NimUB9wP49xnaKYryMV0cZcXrxqicJR4N0NZg2X6Tvpwq4pZhXvkjRDi7VmB8M1uMvX1e0yfGNdLiZ5Eo2Aa5lZYotujcOIb47Qtm2Y41rMtHH3vCLmoJg0_Tyfjek_dvdQOn9uao9UE837fGJG9ecTrkyj1M0S0jJHDwZniKhD501esZXwdQRXkn_l5Z8Q706-0iiX02oNYOJ-qVVpunmAkxVusbf977byObA-Ka3W2&lib=M5XKWvWqSbZVs00Ik85LNLQM4n91EsHj5';

  // 状態管理
  const [rawStreams, setRawStreams] = useState([]); // APIから取得した生データ
  const [filteredStreams, setFilteredStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false); // アニメーション実行済みフラグ
  const [availableDates, setAvailableDates] = useState([]); // 利用可能な日付リスト
  const [selectedDateIndex, setSelectedDateIndex] = useState(0); // 選択された日付のインデックス
  const [selectedStream, setSelectedStream] = useState(null); // 選択されたストリーム
  const [isModalOpen, setIsModalOpen] = useState(false); // モーダルの開閉状態
  const [slideDirection, setSlideDirection] = useState(''); // アニメーション方向
  const [dateAnimation, setDateAnimation] = useState(''); // 日付表示のアニメーション
  const viewMode = 'grid'; // カードタイプ表示に固定
  
  // スワイプ関連の状態管理
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);
  
  // アニメーション管理用の参照
  const animationTimeouts = useRef([]);
  const isAnimating = useRef(false);
  const animationQueue = useRef([]);
  
  // 範囲チェック関数（インデックスベース）
  const canGoPrev = useMemo(() => {
    return selectedDateIndex > 0;
  }, [selectedDateIndex]);
  
  const canGoNext = useMemo(() => {
    return selectedDateIndex < availableDates.length - 1;
  }, [selectedDateIndex, availableDates.length]);

  // データ取得関数（初回のみ）
  const loadStreamData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchStreamData(API_URL);
      setRawStreams(result.streams); // 生データを保存
      
      // JSONファイル通りに全ての日付を表示
      setAvailableDates(result.dates); // 全ての日付を保存
      
      // 最初の日付を選択
      if (result.dates.length > 0) {
        setSelectedDateIndex(0);
      }
    } catch (error) {
      console.error('ストリームデータの取得に失敗しました:', error);
      setRawStreams([]);
      setAvailableDates([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // 初回データ取得のみ
  useEffect(() => {
    loadStreamData(); // 初回実行のみ
  }, [loadStreamData]); // loadStreamDataを依存配列に追加

  // 現在選択されている日付
  const selectedDate = useMemo(() => {
    return availableDates[selectedDateIndex] || null;
  }, [availableDates, selectedDateIndex]);

  // 生データをそのまま使用（ステータス計算を削除）
  const streams = useMemo(() => {
    return rawStreams;
  }, [rawStreams]);

  // お気に入り切り替え関数
  const toggleFavorite = (streamId) => {
    // 生データを更新
    const updatedRawStreams = rawStreams.map(stream => 
      stream.id === streamId 
        ? { ...stream, isFavorite: !stream.isFavorite } 
        : stream
    );
    setRawStreams(updatedRawStreams);
  };

  // すべてのアニメーションタイムアウトをクリアする
  const clearAllAnimations = useCallback(() => {
    animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.current = [];
  }, []);

  // 日付変更処理を実行
  const processDateChange = useCallback((newIndex, direction) => {
    // アニメーションをクリア
    clearAllAnimations();
    setDateAnimation('');
    setSlideDirection('');
    
    // アニメーション開始
    requestAnimationFrame(() => {
      // 日付表示のアニメーション開始
      setDateAnimation(direction);
      
      // コンテンツのアニメーション
      setSlideDirection('fade-out');
      
      // 日付が見えなくなったタイミング（100ms後）でテキストを変更
      animationTimeouts.current.push(
        setTimeout(() => {
          setSelectedDateIndex(newIndex);
        }, 100)
      );
      
      // タイムアウトを配列に追加
      animationTimeouts.current.push(
        setTimeout(() => {
          setSlideDirection(direction);
        }, 150)
      );
      
      animationTimeouts.current.push(
        setTimeout(() => {
          setSlideDirection('');
          isAnimating.current = false;
          
          // キューに次のアニメーションがあれば実行
          if (animationQueue.current.length > 0) {
            const next = animationQueue.current.shift();
            processDateChange(next.newIndex, next.direction);
          }
        }, 350)
      );
      
      // 日付アニメーション完了後にリセット
      animationTimeouts.current.push(
        setTimeout(() => {
          setDateAnimation('');
        }, 200)
      );
    });
  }, [clearAllAnimations]);

  // 日付変更ハンドラー（インデックスベース）
  const handleDateIndexChange = useCallback((newIndex) => {
    // 同じインデックスが選択された場合は何もしない
    if (newIndex === selectedDateIndex) {
      return;
    }
    
    // 現在のインデックスと比較してアニメーション方向を決定
    const direction = newIndex > selectedDateIndex ? 'slide-left' : 'slide-right';
    
    // アニメーション中の場合はキューに追加
    if (isAnimating.current) {
      // キューをクリアして最新のものだけを追加（連続操作時の最適化）
      animationQueue.current = [{ newIndex, direction }];
      return;
    }
    
    // アニメーション開始
    isAnimating.current = true;
    processDateChange(newIndex, direction);
  }, [selectedDateIndex, processDateChange]);
  
  
  // スワイプ用の日付変更（アニメーション付き）
  const goToPrevDayWithAnimation = useCallback(() => {
    if (!canGoPrev) return;
    const newIndex = selectedDateIndex - 1;
    handleDateIndexChange(newIndex);
  }, [canGoPrev, selectedDateIndex, handleDateIndexChange]);
  
  const goToNextDayWithAnimation = useCallback(() => {
    if (!canGoNext) return;
    const newIndex = selectedDateIndex + 1;
    handleDateIndexChange(newIndex);
  }, [canGoNext, selectedDateIndex, handleDateIndexChange]);
  
  // スワイプ設定
  const minSwipeDistance = 50; // 最小スワイプ距離
  const edgeThreshold = 20; // 画面端からの距離
  const verticalThreshold = 2; // 横移動が縦移動の何倍以上か
  
  // タッチ開始
  const handleTouchStart = useCallback((e) => {
    // モーダルが開いている場合はスワイプを無効化
    if (isModalOpen) return;
    
    const touch = e.touches?.[0] || e.targetTouches?.[0];
    if (!touch) return;
    
    // 画面端からの距離をチェック（ブラウザバック対策）
    if (touch.clientX < edgeThreshold || 
        touch.clientX > window.innerWidth - edgeThreshold) {
      return;
    }
    
    setTouchEnd(null);
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
  }, [isModalOpen, edgeThreshold]);
  
  // タッチ移動
  const handleTouchMove = useCallback((e) => {
    if (!touchStart || isModalOpen) return;
    
    const touch = e.touches?.[0] || e.targetTouches?.[0];
    if (!touch) return;
    
    const currentTouch = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    setTouchEnd(currentTouch);
    
    // 移動距離の計算
    const diffX = currentTouch.x - touchStart.x;
    const diffY = currentTouch.y - touchStart.y;
    
    // 横方向の移動が縦方向の移動よりも大きい場合のみスワイプとして処理
    if (Math.abs(diffX) > Math.abs(diffY) * verticalThreshold) {
      // 縦スクロールを防ぐ
      e.preventDefault();
    }
  }, [touchStart, isModalOpen, verticalThreshold]);
  
  // タッチ終了
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd || isModalOpen) {
      return;
    }
    
    const diffX = touchEnd.x - touchStart.x;
    const diffY = touchEnd.y - touchStart.y;
    const timeDiff = Date.now() - touchStart.time;
    const velocity = Math.abs(diffX) / timeDiff;
    
    // スワイプ判定
    if (Math.abs(diffX) > Math.abs(diffY) * verticalThreshold && 
        (Math.abs(diffX) > minSwipeDistance || velocity > 0.5)) {
      if (diffX > 0 && canGoPrev) {
        // 右スワイプ（前の日へ）
        goToPrevDayWithAnimation();
      } else if (diffX < 0 && canGoNext) {
        // 左スワイプ（次の日へ）
        goToNextDayWithAnimation();
      }
    }
    
    // リセット
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, isModalOpen, verticalThreshold, minSwipeDistance, canGoPrev, canGoNext, goToPrevDayWithAnimation, goToNextDayWithAnimation]);

  // 日付によるフィルタリング（APIのグループ日付を使用）
  const filterByDate = (stream, date) => {
    // APIのdisplayDateを使用して日付マッチング（ライブ配信も含めて）
    return stream.displayDate === date;
  };

  // ストリームカードクリック時の処理
  const handleStreamClick = (stream) => {
    setSelectedStream(stream);
    setIsModalOpen(true);
  };

  // モーダルを閉じる処理
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStream(null);
  };

  // 日付フィルター適用
  useEffect(() => {
    let result = streams;
    
    // アーカイブデータを除外
    result = result.filter(stream => !stream.isArchive);
    
    // 日付フィルター適用
    if (selectedDate) {
      result = result.filter(stream => filterByDate(stream, selectedDate));
    }
    
    setFilteredStreams(result);
  }, [streams, selectedDate]);

  // 初回ロードアニメーションの制御
  useEffect(() => {
    if (!loading && filteredStreams.length > 0 && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [loading, filteredStreams.length, hasAnimated]);

  // タッチイベントの登録（passive: false対応）
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    
    // タッチイベントハンドラーをpassive: falseで登録
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  // コンポーネントのアンマウント時にアニメーションをクリア
  useEffect(() => {
    return () => {
      clearAllAnimations();
    };
  }, [clearAllAnimations]);
  
  return (
    <div 
      className="streams-container"
      ref={containerRef}
    >
      <DateSelector 
        availableDates={availableDates}
        selectedIndex={selectedDateIndex}
        onIndexChange={handleDateIndexChange}
        dateAnimation={dateAnimation}
      />

      {loading ? (
        <div className="loading-message">
          <p>配信データを読み込み中...</p>
        </div>
      ) : (
        <StreamsList 
          streams={filteredStreams} 
          toggleFavorite={toggleFavorite} 
          viewMode={viewMode}
          onStreamClick={handleStreamClick}
          slideDirection={slideDirection}
          isFirstLoad={false}
          selectedDate={new Date(selectedDate || new Date())}
          isArchiveMode={false}
        />
      )}
      
      {/* ストリームモーダル */}
      <StreamModal 
        stream={selectedStream}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default StreamsContainer;
